import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { AlertBarService } from '@casb/alert-bar';
import { ButtonModule } from '@sym-ui/toolkit3/button';
import { TableL10n, TableModule } from '@sym-ui/toolkit3/table';
import { ToolbarModule } from '@sym-ui/toolkit3/toolbar';
import { SortEvent } from '@sym-ui/toolkit3/api';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SessionState } from '@casb/session';
import { NotificationsActions } from '../store/notifications.actions';
import { NotificationsState } from '../store/notifications.state';
import { NotificationsStateModule } from '../store/notifications.state.module';
import {
  NotificationInstance,
  NotificationRowDefinition,
  NotificationSetting,
  NotificationTableRow,
} from './notifications.model';
import { ColumnConfig, l10n } from './notifications-table.config';
import { NotificationsDetailsComponent } from './components/notifications-details/notifications-details.component';

@Component({
  selector: 'casb-notifications',
  standalone: true,
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    ButtonModule,
    CommonModule,
    NotificationsDetailsComponent,
    NotificationsStateModule,
    TableModule,
    ToolbarModule,
  ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly alertBar = inject(AlertBarService);

  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(NotificationsState.columns) cols$!: Observable<ColumnConfig[]>;
  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(NotificationsState.tableRows) rows$!: Observable<NotificationTableRow[]>;
  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(NotificationsState.loading) loading$!: Observable<boolean>;
  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(NotificationsState.savePending) savePending$!: Observable<boolean>;
  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(NotificationsState.rowDefinitions) rowDefinitions$!: Observable<
    NotificationRowDefinition[]
  >;

  l10n: TableL10n = l10n;
  selectedColumns: ColumnConfig[] = [];
  rows: NotificationTableRow[] = [];
  allowedDomains: string[] = [];

  selectedRow: NotificationTableRow | null = null;
  selectedInstance: NotificationInstance | null = null;
  selectedRowSettings: NotificationSetting[] = [];
  isFlyoutHidden = true;

  private sortField = '';
  private sortOrder = 1;
  private rowDefinitions: NotificationRowDefinition[] = [];
  private instances: NotificationInstance[] = [];
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.store.dispatch(new NotificationsActions.LoadSummary());

    const session = this.store.selectSnapshot(SessionState.getSession);
    this.allowedDomains = session?.getAllDomains().filter(Boolean) ?? [];

    this.subscriptions.add(
      this.cols$.subscribe((cols) => {
        this.selectedColumns = cols.filter((c) => c.is_default);
      }),
    );

    this.subscriptions.add(
      this.rows$.subscribe((rows) => {
        this.rows = this.applySortOrder([...rows]);
      }),
    );

    this.subscriptions.add(
      this.rowDefinitions$.subscribe((defs) => {
        this.rowDefinitions = defs;
      }),
    );

    this.subscriptions.add(
      this.store
        .select(
          (s: { notifications: { instances: NotificationInstance[] } }) =>
            s.notifications.instances,
        )
        .subscribe((instances) => {
          this.instances = instances;
        }),
    );

    this.subscriptions.add(
      this.actions$
        .pipe(
          ofActionCompleted(NotificationsActions.LoadSummary),
          filter((r) => !!r.result.error),
        )
        .subscribe(() => {
          this.alertBar.errorAlert('Failed to load notification settings.');
        }),
    );

    this.subscriptions.add(
      this.actions$.pipe(ofActionCompleted(NotificationsActions.SaveInstance)).subscribe((r) => {
        this.closeFlyout();
        if (r.result.error) {
          const err = r.result.error as any;
          const message = 'Failed to save notification settings.';
          this.alertBar.errorAlert(message);
        } else {
          this.alertBar.successAlert('Notification settings saved successfully.');
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onRowClick(row: NotificationTableRow): void {
    const isSameRow = this.selectedRow?.spanva_id === row.spanva_id;
    if (isSameRow && !this.isFlyoutHidden) {
      this.closeFlyout();
      return;
    }

    const instance = this.instances.find((i) => i.spanva_id === row.spanva_id) ?? null;
    this.selectedRow = row;
    this.selectedInstance = instance;
    this.selectedRowSettings = instance
      ? NotificationsState.buildFlyoutSettings(this.rowDefinitions, instance)
      : [];
    this.isFlyoutHidden = false;
  }

  closeFlyout(): void {
    this.isFlyoutHidden = true;
    this.selectedRow = null;
    this.selectedInstance = null;
    this.selectedRowSettings = [];
  }

  onSaveNotification(event: {
    spanvaId: string;
    settings: NotificationSetting[];
    recipients: string[];
    masterEnabled: boolean;
  }): void {
    const payload = NotificationsState.buildPatchPayload(
      this.rowDefinitions,
      event.settings,
      event.recipients,
      event.masterEnabled,
    );
    this.store.dispatch(new NotificationsActions.SaveInstance(event.spanvaId, payload));
  }

  onColumnSelectionChange(selectedCols: ColumnConfig[]): void {
    this.selectedColumns = selectedCols;
  }

  onSort(event: SortEvent): void {
    this.sortField = event.field ?? '';
    this.sortOrder = event.order ?? 1;
    if (event.data) {
      event.data.sort((a: NotificationTableRow, b: NotificationTableRow) => {
        const valA = this.getCellValue(a, this.sortField);
        const valB = this.getCellValue(b, this.sortField);
        return valA.localeCompare(valB) * this.sortOrder;
      });
      this.rows = [...event.data];
    }
  }

  private applySortOrder(rows: NotificationTableRow[]): NotificationTableRow[] {
    if (!this.sortField) return rows;
    return rows.sort((a, b) => {
      const valA = this.getCellValue(a, this.sortField);
      const valB = this.getCellValue(b, this.sortField);
      return valA.localeCompare(valB) * this.sortOrder;
    });
  }

  getCellValue(row: NotificationTableRow, field: string): string {
    if (field === 'name') return row.name;
    const ch = row.channelsByUiId[field];
    if (!ch) return '—';
    if (!ch.enabled) return 'Disabled';
    return ch.preset_label !== 'event' ? ch.preset_label : 'Enabled';
  }

  trackBySpanvaId(_index: number, row: NotificationTableRow): string {
    return row.spanva_id;
  }
}
